import { Phone, NotepadText, PhoneOutgoing, Edit } from 'lucide-react';
import Loading from '@/components/Loading';
import Notes from '@/components/Notes/Notes';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useContact from '@/hooks/useContact';
import { IContact } from '@/types';
import { formatPhoneNumber } from '@/utils/helpers';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import CampaignAssociations from './CampaignAssociations';
import ContactCallHistory from './ContactCallHistory';
import ContactForm from './ContactForm';
import ContactListAssociations from './ContactListAssociations';

const ContactCard = ({ contactId }: { contactId: string }) => {
  const contact = useContact(contactId as string);

  return (
    <div className="w-full">
      <SheetContent className="h-full ">
        {contact.isLoading ? (
          <Loading />
        ) : (
          <>
            <SheetHeader className="pb-4 border-b">
              <SheetTitle className="flex items-center justify-between gap-2 text-2xl font-bold">
                <div className="flex items-center w-full gap-1">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={contact.data?.avatar} alt={contact.data?.name || 'NA'} />
                    <AvatarFallback>
                      {contact.data?.name
                        ?.split(' ')
                        .map((n) => n[0])
                        .join('') || 'NA'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    {contact.data?.name || 'Unknown Contact'}{' '}
                    {contact.data?.phone && (
                      <div className="text-sm text-gray-500">{formatPhoneNumber(contact.data?.phone)}</div>
                    )}
                  </div>
                </div>
              </SheetTitle>

              <Card>
                <CardHeader className="pb-0">
                  <CardTitle className="text-lg font-medium">Metadata</CardTitle>
                </CardHeader>
                <CardContent className="py-0 ">
                  {contact.data?.metadata && Object.entries(contact.data.metadata).length ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Key</TableHead>
                          <TableHead>Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(contact.data.metadata).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell>{key}</TableCell>
                            <TableCell>{value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="py-4 text-sm text-gray-500">No metadata found, use edit below to make changes</div>
                  )}
                </CardContent>
              </Card>
            </SheetHeader>
            <ScrollArea className="flex-1">
              <Tabs defaultValue={contact.data.total_calls == 0 ? 'campaigns' : 'calls'} className="w-full mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="campaigns">
                    <PhoneOutgoing className="w-4 h-4 mr-2" />
                    Campaigns
                  </TabsTrigger>

                  <TabsTrigger value="calls">
                    <Phone className="w-4 h-4 mr-2" />
                    History
                  </TabsTrigger>

                  <TabsTrigger value="notes">
                    <NotepadText className="w-4 h-4 mr-2" />
                    Notes
                  </TabsTrigger>
                  <TabsTrigger value="edit">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="calls">
                  <Card>
                    <CardHeader>
                      <CardTitle>Call History</CardTitle>
                      <CardDescription>Here are the calls made to this contact.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ContactCallHistory contactId={contactId} />
                    </CardContent>
                  </Card>
                </TabsContent>
                <TabsContent value="edit">
                  <ContactForm
                    contact={contact.data}
                    onClose={() => {
                      contact.refetch();
                    }}
                  />
                </TabsContent>
                <TabsContent value="notes">
                  <Notes contactId={contactId} />
                </TabsContent>
                <TabsContent value="list">
                  <Card>
                    <CardHeader>
                      <CardTitle>Contact Lists</CardTitle>
                      <CardDescription>Manage contact lists for this contact.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ContactListAssociations contact={contact?.data as IContact} />
                    </CardContent>
                  </Card>
                </TabsContent>
                <TabsContent value="campaigns">
                  <Card>
                    <CardHeader>
                      <CardTitle>Campaigns</CardTitle>
                      <CardDescription>Manage campaigns this contact is associated with.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <CampaignAssociations contact={contact?.data as IContact} />
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </div>
  );
};

export default ContactCard;
